import type { Sort } from '@/components/table/sort';
import type { PageRequest } from '@/models/listPage';
import type { ReferenceName } from '@/stores/useReferenceOptions';

export interface UserFilters {
  /** Chaque mot cherché dans le prénom, le nom ou l'email (côté API : SearchableFilter, sans accents ni casse) */
  search?: string;
  /** Références (ADMIN, ENABLED…) ; null : tous */
  appRole?: string | null;
  userStatus?: string | null;
}

/** Filtres à l'arrivée sur la liste : aucun */
export const DEFAULT_USER_FILTERS: Required<UserFilters> = { search: '', appRole: null, userStatus: null };

/** Id API d'une référence (listes chargées depuis l'API), null si inconnue */
export type ReferenceIdResolver = (name: ReferenceName, reference: string) => number | null;

/** Colonnes triables (le rôle et le statut ne le sont pas : peu de valeurs, filtrées à la place) */
export type UserSortKey = 'name' | 'email' | 'createdAt';

/** À l'arrivée sur la liste : par NOM puis prénom */
export const DEFAULT_USER_SORT: Sort<UserSortKey> = { key: 'name', direction: 'asc' };

/**
 * Requête GET /users d'une page de la liste : filtres, tri et pagination.
 * « Ajouté le » trie sur l'id, qui suit l'ordre de création (unique : pas besoin de départager).
 */
export function buildUserListRequest(
  { search, appRole, userStatus }: UserFilters,
  sort: Sort<UserSortKey>,
  page: number,
  itemsPerPage: number,
  toId: ReferenceIdResolver,
): PageRequest {
  const { direction } = sort;
  const order = {
    name: { lastName: direction, firstName: direction },
    email: { email: direction },
    createdAt: { id: direction },
  }[sort.key] as PageRequest['order'];

  return {
    filters: {
      search: search?.trim() || null,
      appRole: appRole ? toId('appRoles', appRole) : null,
      userStatus: userStatus ? toId('userStatuses', userStatus) : null,
    },
    order,
    page,
    itemsPerPage,
  };
}
